import { useCallback, useEffect, useRef, useState } from 'react'
import { getSettings, saveSettings, getMailFrames } from '../api'
import { stableJson } from '../layout/TabsContext'
import { useToast } from '../layout/ToastContext'
import Panel from '../components/Panel'
import RichTextEditor from '../components/RichTextEditor'
import InfoTip from '../components/InfoTip'
import MailFrameEditor, { type FrameCtl } from '../components/MailFrameEditor'
import BrandMailTexts from '../components/BrandMailTexts'
import { DOCUMENT_URL_TIP, unknownPlaceholderWarning } from '../lookups'
import { fill, PAGES_MAIL_TEMPLATES_TEXTS as T } from '../../shared/texts'
import { SETTINGS_DEFAULTS } from '../../shared/constants'

// System → Vorlagen → E-Mail: the texts of the invoice, single-invoice and credit-note mail, the
// common mail frame and the frame of each brand — „alle Vorlagen auf einen Fleck" (operator,
// 2026-09-27). Until then they sat in Einstellungen (Allgemein and each brand's pane).
//
// ⛔ One changed state for all of it, reported to the page through `bind` (pages/Templates.tsx):
// the ● on the pills, „Übernehmen", „Verwerfen", the guard when the tab is closed. A part with a
// save button of its own is exactly the bug of 2026-09-27 („modified wird nicht ausgelöst").
//
// The texts are saved as a PATCH of just these fields. They are no longer part of the settings
// page's form — a settings tab left open would otherwise write its stale copy back on its next save.

const TEXT_FIELDS = [
    'invoiceMailSubject', 'invoiceMailBody',
    'singleInvoiceMailSubject', 'singleInvoiceMailBody',
    'creditNoteMailSubject', 'creditNoteMailBody',
    'exportNoMovementLine', 'pspBalanceUnavailableNote', 'exportReportFilePrefix', 'invoicePdfNamePattern',
] as const
type Texts = Record<(typeof TEXT_FIELDS)[number], string>

const pickTexts = (s: any): Texts =>
    Object.fromEntries(TEXT_FIELDS.map((k) => [k, s?.[k] ?? ''])) as Texts

export interface MailTemplatesCtl {
    dirty: boolean
    save: () => Promise<void>
    discard: () => void
}

export default function MailTemplates({ readOnly, bind }: { readOnly: boolean, bind: (ctl: MailTemplatesCtl | null) => void }) {
    const { toast } = useToast()
    const [texts, setTexts] = useState<Texts | null>(null)
    const [baseline, setBaseline] = useState('')
    const [error, setError] = useState('')
    const [brands, setBrands] = useState<{ brand: string, name: string }[]>([])
    // The tab of each panel: '' = Allgemein (common), otherwise a brand key (operator, 2026-10-26:
    // „Reiter Header: Allgemein | deja | MSBC | Gfv … Mit dem Mailrahmen dasselbe"). The brand
    // labels come from the brands (getMailFrames), not from here.
    const [textTab, setTextTab] = useState('')
    const [frameTab, setFrameTab] = useState('')
    // Bumped after a save so both frame editors load afresh (the brand editor shows which frame
    // its mails use — after saving the common one that may have changed).
    const [version, setVersion] = useState(0)

    const load = useCallback(async () => {
        try {
            const [s, f] = await Promise.all([getSettings(), getMailFrames()])
            const t = pickTexts(s)
            setTexts(t)
            setBaseline(stableJson(t))
            setBrands(f.brands.map((b) => ({ brand: b.brand, name: b.name })))
            setError('')
        } catch (e: any) { setError(e?.message ?? T.loadFailed) }
    }, [])
    useEffect(() => { void load() }, [load])

    // Three parts report their changed state: the common frame, the brand's frame and the brand's
    // own mail texts (components/BrandMailTexts.tsx, since 2026-10-26).
    const frameCtl = useRef<{ common: FrameCtl | null, brand: FrameCtl | null, brandTexts: FrameCtl | null }>({ common: null, brand: null, brandTexts: null })
    const [frameDirty, setFrameDirty] = useState({ common: false, brand: false, brandTexts: false })
    const bindCommon = useCallback((ctl: FrameCtl | null) => {
        frameCtl.current.common = ctl
        setFrameDirty((d) => (d.common === !!ctl?.dirty ? d : { ...d, common: !!ctl?.dirty }))
    }, [])
    const bindBrand = useCallback((ctl: FrameCtl | null) => {
        frameCtl.current.brand = ctl
        setFrameDirty((d) => (d.brand === !!ctl?.dirty ? d : { ...d, brand: !!ctl?.dirty }))
    }, [])

    const bindBrandTexts = useCallback((ctl: FrameCtl | null) => {
        frameCtl.current.brandTexts = ctl
        setFrameDirty((d) => (d.brandTexts === !!ctl?.dirty ? d : { ...d, brandTexts: !!ctl?.dirty }))
    }, [])

    const textsDirty = !!texts && stableJson(texts) !== baseline
    const dirty = !readOnly && (textsDirty || frameDirty.common || frameDirty.brand || frameDirty.brandTexts)
    // Only the open tab of a panel can hold changes — the others are not mounted.
    const textTabDirty = textTab === '' ? textsDirty : frameDirty.brandTexts
    const frameTabDirty = frameTab === '' ? frameDirty.common : frameDirty.brand

    const save = async () => {
        if (textsDirty && texts) {
            const saved = await saveSettings({ ...texts })
            const t = pickTexts(saved)
            setTexts(t)
            setBaseline(stableJson(t))
            // Only the six mail texts: the export texts have placeholders of their own.
            const warning = unknownPlaceholderWarning(t.invoiceMailSubject, t.invoiceMailBody, t.singleInvoiceMailSubject,
                t.singleInvoiceMailBody, t.creditNoteMailSubject, t.creditNoteMailBody)
            if (warning) toast(warning, 'warn')
        }
        if (frameDirty.common) await frameCtl.current.common?.save()
        if (frameDirty.brand) await frameCtl.current.brand?.save()
        if (frameDirty.brandTexts) await frameCtl.current.brandTexts?.save()
        setVersion((v) => v + 1)
    }
    const discard = () => {
        if (baseline) setTexts(JSON.parse(baseline))
        frameCtl.current.common?.discard()
        frameCtl.current.brand?.discard()
        frameCtl.current.brandTexts?.discard()
    }
    const latest = useRef({ save, discard })
    latest.current = { save, discard }
    useEffect(() => {
        bind({ dirty, save: () => latest.current.save(), discard: () => latest.current.discard() })
    }, [dirty, bind])
    useEffect(() => () => bind(null), [bind])

    // Switching a tab would throw away what is typed in it — the same rule as the settings page's pills.
    const select = (cur: string, next: string, tabDirty: boolean, setTab: (t: string) => void) => {
        if (next === cur) return
        if (tabDirty) { toast(T.applyOrDiscardFirst, 'warn'); return }
        setTab(next)
    }
    const tabs = (cur: string, tabDirty: boolean, setTab: (t: string) => void) => (
        <div className="pill-tabs" role="tablist" aria-label={T.tabsLabel} style={{ marginBottom: 12 }}>
            {[{ brand: '', name: T.tabCommon }, ...brands].map((b) => {
                const active = b.brand === cur
                const blocked = tabDirty && !active
                return (
                    <button key={b.brand || 'common'} type="button" role="tab" className={`pill-tab${active ? ' active' : ''}`}
                        style={blocked ? { opacity: 0.45 } : undefined}
                        title={blocked ? T.pillBlocked : undefined}
                        onClick={() => select(cur, b.brand, tabDirty, setTab)}>
                        {b.name}{active && tabDirty ? ' ●' : ''}
                    </button>
                )
            })}
        </div>
    )

    if (error) return <div className="notice err">{error}</div>
    if (!texts) return <p className="muted">{T.loading}</p>
    const set = (k: keyof Texts, v: string) => setTexts((t) => (t ? { ...t, [k]: v } : t))

    return (
        <>
            <Panel title={T.textsPanel}>
                {tabs(textTab, textTabDirty, setTextTab)}
                {textTab !== '' && <BrandMailTexts key={`${textTab}-texts-${version}`} brand={textTab} readOnly={readOnly} bind={bindBrandTexts} />}
                {/* Only the texts are disabled for the read-only role; the tabs must stay clickable,
                    and the frame editors handle read-only themselves. */}
                {textTab === '' && <fieldset disabled={readOnly} style={{ border: 0, padding: 0, margin: 0 }}>
                <h3 style={{ marginTop: 0 }}>{T.invoiceHeading}</h3>
                <div className="field">
                    <label>{T.subject} <InfoTip>{T.invoiceSubjectTip}</InfoTip></label>
                    <input type="text" name="invoice-mail-subject" autoComplete="off"
                        value={texts.invoiceMailSubject} onChange={(e) => set('invoiceMailSubject', e.target.value)} />
                </div>
                <div className="field">
                    <label>{T.standardText} <InfoTip>{T.invoiceBodyTip} {DOCUMENT_URL_TIP}</InfoTip></label>
                    <RichTextEditor value={texts.invoiceMailBody} onChange={(html) => set('invoiceMailBody', html)} />
                </div>

                <h3>{T.singleHeading}</h3>
                <p className="muted" style={{ marginTop: 0 }}>
                    {T.singleIntro1}<b>{T.singleIntroOne}</b>{T.singleIntro2}<b>{T.singleIntroBoth}</b>{T.singleIntro3}
                </p>
                <div className="field">
                    <label>{T.subject} <InfoTip>{T.singleSubjectTip}</InfoTip></label>
                    <input type="text" name="single-invoice-mail-subject" autoComplete="off"
                        value={texts.singleInvoiceMailSubject} onChange={(e) => set('singleInvoiceMailSubject', e.target.value)} />
                </div>
                <div className="field">
                    <label>{T.text} <InfoTip>{T.singleBodyTip} {DOCUMENT_URL_TIP}</InfoTip></label>
                    <RichTextEditor value={texts.singleInvoiceMailBody} onChange={(html) => set('singleInvoiceMailBody', html)} />
                </div>

                <h3>{T.creditHeading}</h3>
                <div className="field">
                    <label>{T.subject} <InfoTip>{T.creditSubjectTip}</InfoTip></label>
                    <input type="text" name="credit-note-mail-subject" autoComplete="off"
                        value={texts.creditNoteMailSubject} onChange={(e) => set('creditNoteMailSubject', e.target.value)} />
                </div>
                <div className="field">
                    <label>{T.text} <InfoTip>{T.creditBodyTip} {DOCUMENT_URL_TIP}</InfoTip></label>
                    <RichTextEditor value={texts.creditNoteMailBody} onChange={(html) => set('creditNoteMailBody', html)} />
                </div>

                <h3>{T.exportHeading}</h3>
                <div className="field">
                    <label>{T.noMovementLabel} <InfoTip>{T.noMovementTip}</InfoTip></label>
                    <input type="text" name="export-no-movement-line" autoComplete="off"
                        value={texts.exportNoMovementLine} onChange={(e) => set('exportNoMovementLine', e.target.value)} />
                </div>
                <div className="field">
                    <label>{T.balanceLabel} <InfoTip>{T.balanceTip}</InfoTip></label>
                    <input type="text" name="psp-balance-unavailable-note" autoComplete="off"
                        value={texts.pspBalanceUnavailableNote} onChange={(e) => set('pspBalanceUnavailableNote', e.target.value)} />
                </div>
                <div className="field">
                    <label>{T.reportPrefixLabel} <InfoTip>{fill(T.reportPrefixTip, { prefix: SETTINGS_DEFAULTS.exportReportFilePrefix })}</InfoTip></label>
                    <input type="text" name="export-report-file-prefix" autoComplete="off"
                        value={texts.exportReportFilePrefix} onChange={(e) => set('exportReportFilePrefix', e.target.value)} />
                </div>
                <div className="field">
                    <label>{T.pdfNameLabel} <InfoTip>{fill(T.pdfNameTip, {
                        brand: '{brand}', ref: '{ref}', yyyy: '{yyyy}', yy: '{yy}', mm: '{mm}', dd: '{dd}', counter: '{counter}', number: '{number}',
                        pattern: SETTINGS_DEFAULTS.invoicePdfNamePattern,
                    })}</InfoTip></label>
                    <input type="text" name="invoice-pdf-name-pattern" autoComplete="off" placeholder={SETTINGS_DEFAULTS.invoicePdfNamePattern}
                        value={texts.invoicePdfNamePattern} onChange={(e) => set('invoicePdfNamePattern', e.target.value)} />
                </div>
                </fieldset>}
            </Panel>

            <Panel title={T.framePanel}>
                {tabs(frameTab, frameTabDirty, setFrameTab)}
                {frameTab === '' && <MailFrameEditor key={`common-${version}`} mode="common" readOnly={readOnly} bind={bindCommon} />}
                {frameTab !== '' && <MailFrameEditor key={`${frameTab}-${version}`} mode="brand" brand={frameTab} readOnly={readOnly} bind={bindBrand} />}
            </Panel>
        </>
    )
}
